import { useState, useEffect, useCallback, useRef } from 'react'

const STORAGE_KEY = 'yokan_timeline_last_visit'
const POLL_MS = 60_000

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

function getCachedLastVisit() {
  try { return localStorage.getItem(STORAGE_KEY) ?? new Date(0).toISOString() } catch { return new Date(0).toISOString() }
}

function setCachedLastVisit(iso) {
  try { localStorage.setItem(STORAGE_KEY, iso) } catch {}
}

export function useNotificationBadge() {
  const [unreadCount, setUnreadCount]           = useState(0)
  const [unreadByCategory, setUnreadByCategory] = useState({})
  const lastVisitRef = useRef(getCachedLastVisit())

  // On mount: fetch the server-side read_at and use whichever is newer
  useEffect(() => {
    fetch(`${API_BASE}/events/read-at`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.readAt) return
        const serverTs = new Date(data.readAt).toISOString()
        const localTs  = lastVisitRef.current
        const newer    = serverTs > localTs ? serverTs : localTs
        lastVisitRef.current = newer
        setCachedLastVisit(newer)
      })
      .catch(() => {})
  }, [])

  const fetchCount = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE}/events/count?since=${encodeURIComponent(lastVisitRef.current)}`,
        { credentials: 'include' }
      )
      if (!res.ok) return
      const { total, byCategory } = await res.json()
      setUnreadCount(total)
      setUnreadByCategory(byCategory)
    } catch {}
  }, [])

  useEffect(() => {
    fetchCount()
    const id = setInterval(fetchCount, POLL_MS)
    return () => clearInterval(id)
  }, [fetchCount])

  const markRead = useCallback(async () => {
    const now = new Date().toISOString()
    lastVisitRef.current = now
    setCachedLastVisit(now)
    setUnreadCount(0)
    setUnreadByCategory({})
    try {
      await fetch(`${API_BASE}/events/mark-read`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch {}
  }, [])

  return { unreadCount, unreadByCategory, markRead }
}
